import type {
  IRepaymentPlanData,
  IRepaymentPlanEntryData,
  IRepaymentPlanTotalsData,
} from "./types";

const round2 = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const money = (value: number): string => round2(value).toFixed(2);

export interface IEarlyRepaymentPlan {
  entries: IRepaymentPlanEntryData[];
  totals: IRepaymentPlanTotalsData;
  isEarly: boolean;
  monthsSaved: number;
  interestSaved: number;
  feesSaved: number;
  grossSaved: number;
  /** The lender's charge for settling at month N, as the backend computed it
   * for this bid's terms. Null when not computable. */
  settlementFee: number | null;
  /** What settling early is actually worth: gross saved minus the settlement
   * fee. Can be negative — settling early can cost more than it saves, and
   * that must be visible rather than rounded away.
   *
   * Equals core-service's own `totals.totalPayment - totalCostIfSettled`:
   * that expands to (payments after N) - balance - fee, and the principal
   * after N IS the balance, leaving interest + fees skipped, minus the fee.
   * So this is the backend's figure, derived from fields we already hold.
   * If it is ever swapped for `totalCostIfSettled` directly, note that field
   * is INCLUSIVE of the fee — adding the fee again double-counts it. */
  netSaved: number;
}

const sumBy = (
  list: IRepaymentPlanEntryData[],
  pick: (entry: IRepaymentPlanEntryData) => number,
): number => list.reduce((acc, entry) => acc + pick(entry), 0);

/**
 * Truncate the backend plan at repayment-month N, ballooning that row's
 * principal to the full balance; full term is a no-op. The backend plan leads
 * with a month-0 row (setup fee), so repayment months are `entries[1..]` and
 * month 0 is always kept. Each saving (interest, fees) is the full term's
 * total for that column minus what settling here actually charges.
 *
 * The settlement fee the backend computed for month N is charged on top, so it
 * lands in that row's payment and in the totals, and `netSaved` reports the
 * saving after it. It is neither interest nor an administration fee, so it does
 * not disturb the two savings figures above — only what is finally owed.
 */
export const deriveEarlyRepaymentPlan = (
  plan: IRepaymentPlanData,
  months: number,
): IEarlyRepaymentPlan => {
  const repaymentMonths = plan.entries.length - 1;
  const n = Math.min(Math.max(Math.trunc(months), 1), repaymentMonths);

  if (n >= repaymentMonths) {
    return {
      entries: plan.entries,
      totals: plan.totals,
      isEarly: false,
      monthsSaved: 0,
      interestSaved: 0,
      feesSaved: 0,
      grossSaved: 0,
      settlementFee: null,
      netSaved: 0,
    };
  }

  const kept = plan.entries.slice(0, n + 1);
  const last = kept[n] as IRepaymentPlanEntryData;

  const payoffPrincipal =
    Number(last.principal) + Number(last.remainingBalance);
  // What the customer owes at settlement is the interest accrued to this month,
  // not the amount this row happened to charge. On a back-loaded plan a
  // non-payment month charges 0.00 while real interest has accrued — reading
  // `interest` here would render "settle now, pay no interest". BE guarantees
  // accruedInterest == interest on any payment month, so this is exact for
  // MONTHLY too rather than a special case.
  const interestN = Number(last.accruedInterest);
  // Same reasoning for the recurring fee: it defers alongside interest, so a
  // skipped month charges 0.00 while fees have accrued.
  const feesN = Number(last.accruedAdministrationFees);
  // The charge for settling here. It belongs in this row's payment and in
  // every total: leaving it out understated "total to repay" by exactly the
  // fee, and left the savings block claiming a gain the fee had cancelled.
  const settlementFee =
    last.earlySettlementFee != null ? Number(last.earlySettlementFee) : null;
  const settlementFeeDue = settlementFee ?? 0;

  const balloonRow: IRepaymentPlanEntryData = {
    ...last,
    interest: money(interestN),
    principal: money(payoffPrincipal),
    // Rewritten to the accrued figure, which feesSaved below depends on.
    administrationFees: money(feesN),
    payment: money(interestN + payoffPrincipal + feesN + settlementFeeDue),
    remainingBalance: "0.00",
  };

  const entries = [...kept.slice(0, n), balloonRow];

  const totalInterest = sumBy(entries, (e) => Number(e.interest));
  const totalPrincipal = sumBy(entries, (e) => Number(e.principal));
  const totalAdministration = sumBy(entries, (e) =>
    Number(e.administrationFees),
  );
  const setupFee = Number(plan.totals.setupFee);
  const amount = Number(plan.totals.totalPrincipal);

  const totalPayment = sumBy(entries, (e) => Number(e.payment));
  const loanPayment = totalInterest + totalPrincipal;
  const setupFeePaidUpfront = plan.setupFeeFinanced ? 0 : setupFee;
  const totalRepaid =
    loanPayment + totalAdministration + setupFeePaidUpfront + settlementFeeDue;
  const totalCostOfCredit = totalRepaid - amount;

  const totals: IRepaymentPlanTotalsData = {
    totalPayment: money(totalPayment),
    totalInterest: money(totalInterest),
    totalPrincipal: money(totalPrincipal),
    totalAdministrationFee: money(totalAdministration),
    setupFee: money(setupFee),
    totalRepaid: money(totalRepaid),
    totalCostOfCredit: money(totalCostOfCredit),
  };

  // Full-term total minus what settling charges — never the sum of the skipped
  // rows, which on a back-loaded plan is the whole term's amount and would read
  // as "you saved all of it". Fees rely on the balloon row above rewriting
  // `administrationFees` to the accrued figure.
  const interestSaved = round2(
    sumBy(plan.entries, (e) => Number(e.interest)) - totalInterest,
  );
  const feesSaved = round2(
    sumBy(plan.entries, (e) => Number(e.administrationFees)) -
      totalAdministration,
  );
  const grossSaved = round2(interestSaved + feesSaved);
  const netSaved = round2(grossSaved - settlementFeeDue);

  return {
    entries,
    totals,
    isEarly: true,
    monthsSaved: repaymentMonths - n,
    interestSaved,
    feesSaved,
    grossSaved,
    settlementFee,
    netSaved,
  };
};
